import { morph } from '@theme/morph';
import { fetchWithRetry } from '@theme/utilities';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  /**
   * Track active fetch count
   * @type {number}
   */
  #activeFetchCount = 0;

  /**
   * Maximum concurrent fetches
   * @type {number}
   */
  MAX_CONCURRENT_FETCHES = 2;

  /**
   * Abort controllers for individual fetches
   * @type {Map<string, AbortController>}
   */
  #fetchAbortControllers = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);

      morphSection(sectionId, sectionHTML);
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    // Concurrent limit: Wait if too many fetches are active
    while (this.#activeFetchCount >= this.MAX_CONCURRENT_FETCHES) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Cancel any existing fetch for this URL
    const existingController = this.#fetchAbortControllers.get(sectionUrl);
    if (existingController) {
      existingController.abort();
      this.#fetchAbortControllers.delete(sectionUrl);
    }

    // Create new abort controller for this fetch
    const abortController = new AbortController();
    this.#fetchAbortControllers.set(sectionUrl, abortController);

    // Increment active fetch count
    this.#activeFetchCount++;

    pendingPromise = fetchWithRetry(sectionUrl, {}, 3, 500, 10000, abortController.signal)
      .then((response) => {
        return response.text();
      })
      .then((html) => {
        // Validate that we got actual HTML content
        if (!html || html.trim().length === 0) {
          throw new Error('Empty response received');
        }

        // Basic validation that this looks like a valid section response
        if (!html.includes('shopify-section')) {
          console.warn('Response does not appear to contain a valid Shopify section:', sectionUrl);
        }

        return html;
      })
      .catch((error) => {
        // Clean up the pending promise on error
        this.#pendingPromises.delete(sectionUrl);
        this.#fetchAbortControllers.delete(sectionUrl);
        this.#activeFetchCount = Math.max(0, this.#activeFetchCount - 1);

        // Don't log errors for aborted requests
        if (error.name === 'AbortError') {
          console.log('Fetch aborted for:', sectionUrl);
          throw error;
        }

        console.error('Failed to fetch section after retries:', sectionUrl, error);

        // Return cached content if available, otherwise throw
        const cachedHTML = this.#cache.get(sectionUrl);
        if (cachedHTML) {
          console.log('Using cached content as fallback for:', sectionUrl);
          return cachedHTML;
        }

        throw error;
      });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    try {
      const sectionHTML = await pendingPromise;
      this.#pendingPromises.delete(sectionUrl);
      this.#fetchAbortControllers.delete(sectionUrl);
      this.#activeFetchCount = Math.max(0, this.#activeFetchCount - 1);

      if (useCache) {
        this.#cache.set(sectionUrl, sectionHTML);
      }
      return sectionHTML;
    } catch (error) {
      // Final cleanup
      this.#pendingPromises.delete(sectionUrl);
      this.#fetchAbortControllers.delete(sectionUrl);
      this.#activeFetchCount = Math.max(0, this.#activeFetchCount - 1);
      throw error;
    }
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);
}

export const sectionRenderer = new SectionRenderer();